import React, { useState } from "react";
import { Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, IconButton, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { useNavigate } from "react-router-dom";
import { planManagementText } from "../../../constants/data";
import Modal from "../../../components/Modal";
import styles from "./style.js";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#28AA4A",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

const PlanManagementPage = ({
  plans = [],
  loading = false,
  selectedCountry = "",
  selectedCurrency = "",
  onEditPlan,
  onTogglePlanStatus,
  onDeletePlan,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const [actionModal, setActionModal] = useState({
    open: false,
    action: "",
    planId: "",
    currentStatus: true,
  });
  const role = localStorage.getItem("role");
  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditSubscription = !readOnly && (role === "SuperAdmin" ||
    permissions?.subscriptions?.edit === true);

  const handleCreateNewPlan = () => {
    navigate("/dashboard/planManagement/createPlan", {
      state: selectedCountry
        ? {
          country: selectedCountry,
          currency: selectedCurrency || "",
        }
        : undefined,
    });
  };

  const handleEditPlan = (planId) => {
    if (onEditPlan) {
      onEditPlan(planId);
    }
  };

  const openActionModal = (action, planId, currentStatus) => {
    setActionModal({
      open: true,
      action,
      planId,
      currentStatus,
    });
  };

  const closeActionModal = () => {
    setActionModal({
      open: false,
      action: "",
      planId: "",
      currentStatus: true,
    });
  };

  const handleActionConfirm = () => {
    if (!actionModal.planId) return;

    if (actionModal.action === "toggle" && onTogglePlanStatus) {
      onTogglePlanStatus(actionModal.planId, actionModal.currentStatus);
    }

    if (actionModal.action === "delete" && onDeletePlan) {
      onDeletePlan(actionModal.planId);
    }

    closeActionModal();
  };

  const isToggleAction = actionModal.action === "toggle";
  const modalTitle = isToggleAction
    ? `${actionModal.currentStatus ? "Disable" : "Activate"} Plan`
    : planManagementText.deleteModalTitle || "Delete Plan";
  const modalBody = isToggleAction
    ? `Are you sure you want to ${actionModal.currentStatus ? "disable" : "activate"} this plan?`
    : planManagementText.deleteModalBody || "Are you sure you want to delete this plan?";
  const modalConfirm = isToggleAction
    ? actionModal.currentStatus ? "Disable" : "Activate"
    : planManagementText.deleteModalConfirm || "Delete";

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box>
          <Typography sx={styles.title}>{readOnly ? "Subscriptions" : planManagementText.title}</Typography>
          <Typography sx={styles.subtitle}>
            {selectedCountry
              ? `${planManagementText.subtitle} (${selectedCountry})`
              : planManagementText.subtitle}
          </Typography>
        </Box>
        {canEditSubscription && (
          <Button
            variant="contained"
            onClick={handleCreateNewPlan}
            sx={styles.addButton}
          >
            {planManagementText.createButton}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {planManagementText.tableHeaders
                .filter(
                  (header) =>
                    canEditSubscription ||
                    header.toLowerCase() !== "Actions".toLowerCase()
                )
                .map((header) => (
                  <TableCell key={header} sx={styles.tableHeaderCell}>
                    {header}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={planManagementText.tableHeaders.length} align="center" sx={styles.emptyCell}>
                  <Typography>
                    {selectedCountry
                      ? `No plans found for ${selectedCountry}.`
                      : planManagementText.emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan, index) => {
                const isActive = plan.isActive !== false;
                const employeeLimit =
                  plan?.moduleLimits?.activeEmployees ??
                  plan?.activeEmployees ??
                  plan?.employeeLimit ??
                  planManagementText.fallbacks.employeeLimit;
                const payslipLimit =
                  plan?.moduleLimits?.payslipLimit ??
                  plan?.payslipLimit ??
                  plan?.payslipsLimit ??
                  planManagementText.fallbacks.payslipLimit;
                // New model: price lives in pricing[] per country (base price is
                // always $0). Prefer the selected country's active price; fall
                // back to a single active entry, "Multiple" when several, then
                // the legacy base fields.
                const normalizeCn = (s) => String(s || "").trim().toLowerCase();
                const activePricing = Array.isArray(plan?.pricing)
                  ? plan.pricing.filter((p) => p?.isActive === true)
                  : [];
                let displayPrice = null;
                let displayCurrency = "";
                let isMultiCountry = false;
                const countryMatch =
                  selectedCountry &&
                  activePricing.find(
                    (p) => normalizeCn(p.country) === normalizeCn(selectedCountry)
                  );
                if (countryMatch) {
                  displayPrice = countryMatch.price;
                  displayCurrency = countryMatch.currency;
                } else if (activePricing.length === 1) {
                  displayPrice = activePricing[0].price;
                  displayCurrency = activePricing[0].currency;
                } else if (activePricing.length > 1) {
                  isMultiCountry = true;
                } else {
                  displayPrice = plan?.price ?? plan?.basePrice;
                  displayCurrency = plan?.currency || plan?.baseCurrency;
                }
                const hasPrice =
                  displayPrice !== undefined && displayPrice !== null && displayPrice !== "";

                return (
                  <TableRow
                    key={plan._id || plan.id || index}
                    sx={{
                      ...styles.tableRow,
                      ...(!isActive ? styles.inactiveRow : {}),
                    }}
                  >
                    <TableCell sx={styles.tableCell}>
                      {plan.planName || planManagementText.fallbacks.planName}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {plan.billingCycle || planManagementText.fallbacks.billingCycle}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {employeeLimit}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {payslipLimit}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {isMultiCountry
                        ? `${activePricing.length} countries`
                        : displayCurrency && hasPrice
                        ? `${displayCurrency} ${displayPrice}`
                        : hasPrice
                        ? displayPrice
                        : planManagementText.fallbacks.price}
                    </TableCell>
                    {canEditSubscription && (
                      <TableCell sx={styles.tableCell}>
                        <Box sx={styles.actionButtons}>
                          <IconButton
                            onClick={() => handleEditPlan(plan._id || plan.id)}
                            sx={{
                              ...styles.editButton,
                              ...(!isActive ? { cursor: "not-allowed", opacity: 0.6 } : {}),
                            }}
                            size="small"
                            disabled={!isActive}
                          >
                            <EditIcon sx={styles.editIcon} />
                          </IconButton>

                          <BlockOutlinedIcon
                            sx={{
                              color: isActive ? "#ff0000" : "#BDBDBD",
                              ml: 1,
                              cursor: "pointer",
                            }}
                            fontSize="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openActionModal("toggle", plan._id || plan.id, isActive);
                            }}
                          />

                          <DeleteOutlineIcon
                            sx={{
                              color: "#ff0000",
                              ml: 1,
                              cursor: "pointer",
                            }}
                            fontSize="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openActionModal("delete", plan._id || plan.id);
                            }}
                          />
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={styles.tableCell}>
                      <AntSwitch
                        checked={isActive}
                        disabled={!canEditSubscription}
                        onChange={(e) => {
                          e.stopPropagation();

                          if (canEditSubscription) {
                            openActionModal("toggle", plan._id || plan.id, isActive);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={actionModal.open}
        setOpen={closeActionModal}
        deletetText={modalConfirm}
        cancelText={planManagementText.modalCancel}
        title={modalTitle}
        handleDelete={handleActionConfirm}
      >
        <Box>
          <Typography>{modalBody}</Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default PlanManagementPage;
